import React, { Fragment, useEffect, useState } from 'react';

import { useTheme } from '../../../../../app/hooks/useTheme';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { generateNanoId } from '../../../../../utils/generateNanoId';
import { SentryLogger } from '../../../../../utils/SentryLogger';

import {
  PayoneerCheckoutPayment,
  PayoneerCheckoutPaymentBeforeOtherWidgetRun,
  PayoneerCheckoutPaymentListUrl,
  PayoneerCheckoutPaymentLongId
} from '../../../../payoneerCheckout/utils/PayoneerCheckoutPayment';
import {
  PAYONEER_CHECKOUT_SDK_VERSION,
  payButtonContainerId,
  payButtonId,
  paymentContainerId,
  paymentNetworksId
} from '../../../../payoneerCheckout/utils/PayoneerCheckoutPayment/payoneerCheckoutPaymentConstants';

const sentryLogger = new SentryLogger('payoneer-checkout', {
  defaultTags: { 'payoneer-checkout-sdk': PAYONEER_CHECKOUT_SDK_VERSION }
});

interface InvoicePayoneerCheckoutPaymentContainerProps {
  longId?: PayoneerCheckoutPaymentLongId;
  listUrl?: PayoneerCheckoutPaymentListUrl;
  widgetId?: string;
  beforeOtherWidgetRun?: PayoneerCheckoutPaymentBeforeOtherWidgetRun;
}

function InvoicePayoneerCheckoutPaymentContainer({
  longId,
  listUrl,
  widgetId,
  beforeOtherWidgetRun
}: InvoicePayoneerCheckoutPaymentContainerProps) {
  const [paymentKey, setPaymentKey] = useState<string>(generateNanoId());

  const { theme } = useTheme();

  const isWebSdk = PAYONEER_CHECKOUT_SDK_VERSION === 'web';
  const hasRequiredInfo = isWebSdk ? Boolean(longId) : Boolean(listUrl);

  useEffect(() => {
    if (!hasRequiredInfo) {
      sentryLogger.captureMessage(
        'InvoicePayoneerCheckoutPaymentContainer: missing checkout info',
        {
          stage: 'invoice-container-missing-info',
          extra: {
            sdkVersion: PAYONEER_CHECKOUT_SDK_VERSION,
            hasLongId: Boolean(longId),
            hasListUrl: Boolean(listUrl),
            widgetId
          }
        }
      );
      return;
    }

    sentryLogger.breadcrumb(
      'InvoicePayoneerCheckoutPaymentContainer: running widget',
      {
        sdkVersion: PAYONEER_CHECKOUT_SDK_VERSION,
        widgetId,
        theme,
        hasLongId: Boolean(longId),
        hasListUrl: Boolean(listUrl)
      }
    );

    PayoneerCheckoutPayment.runWidget({
      longId,
      listUrl,
      theme,
      widgetId,
      beforeOtherWidgetRun
    }).catch(() => {
      // Already captured inside PayoneerCheckoutPayment.runWidget — swallow
      // to avoid an unhandled promise rejection.
    });

    return () => {
      setPaymentKey(generateNanoId());
    };
  }, [theme, longId, listUrl, widgetId, beforeOtherWidgetRun, hasRequiredInfo]);

  if (!hasRequiredInfo) {
    return (
      <AlertMessage
        addClassName="mt-2"
        message="Can't get info for payoneer checkout"
      />
    );
  }

  if (isWebSdk) {
    return (
      <Fragment key={paymentKey}>
        <div id={paymentContainerId} className="payoneer-checkout-container" />
      </Fragment>
    );
  }

  return (
    <Fragment key={paymentKey}>
      <div id={paymentNetworksId} className="payment-networks-container" />
      <div id={payButtonContainerId} className="submit-buttons-container">
        <button
          type="button"
          id={payButtonId}
          className="py-2 pl-2 pr-4 space-x-1 rounded-md flex m-auto items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base text-white bg-blue-600 shadow-sm hover:bg-blue-500 focus:bg-blue-500 hover:shadow-md w-72 max-w-full justify-center bg-blue-600 hover:bg-blue-700"
        />
      </div>
    </Fragment>
  );
}

export default InvoicePayoneerCheckoutPaymentContainer;
